import {
	Badge,
	Box,
	Button,
	Inline,
	Icons,
	Text,
	Heading,
	Stack,
	Col,
	Dropdown,
	Grid,
	joinAttributes,
	Spinner,
	Time,
	Component,
	Money,
	Alert,
	Select,
} from "@sembark-travel/ui/base"
import {
	Form,
	withServerErrors,
	validateFormValues,
	FieldArray,
	GetFieldValue,
	SelectField,
	TextAreaInputField,
	SwitchInputField,
	arrayMutators,
	SubmissionError,
} from "@sembark-travel/ui/form"
import { Optional, Required } from "utility-types"
import {
	dateToQuery,
	localOrUtcTimestampToLocalDate,
	addUnit,
	startOf,
	parseDate,
	createDuration,
	formatDate,
	utcTimestampToLocalDateTimeString,
} from "@sembark-travel/datetime-utils"
import { isNumeric, withOrdinalSuffix } from "@sembark-travel/number-utils"
import { NavLink, queryToSearch, Link } from "@sembark-travel/ui/router"
import { useXHR } from "@sembark-travel/xhr"
import useSWR from "swr"
import { PERMISSIONS, useCheckPermissions } from "../Auth"
import { Email, PhoneNumber } from "../Contacts"
import { generatePath } from "../router-utils"
import {
	AssignServiceProvidersInDialog,
	ICabSchedule,
	ScheduleDetailsInDialog,
	getEndDateTimeOfCabSchedule,
	getStartDateTimeOfCabSchedule,
} from "./../CabScheduling"
import {
	TDaywiseOperationalBookingService,
	TTripOperationalBooking,
} from "./store"
import {
	AssignTravelActivityBookingProvidersInDialog,
	TravelActivityBookingDetailsInDialog,
} from "../TravelActivityBookings"
import {
	ReorderServicesInDialog,
	TripOperationalBookingsItemForDateInDialog,
} from "./ForDateItem"
import { ShareTripOperationalBookings } from "./ShareOperationalBookings"
import { showSnackbar } from "@sembark-travel/ui/snackbar"
import { useMemo } from "react"
import { IPaymentable } from "../Payments"
import { Dialog, useDialog } from "@sembark-travel/ui/dialog"
import {
	ITransportService,
	TTransportServiceLocation,
} from "../TransportServices"
import {
	CabCapacityWarningMessage,
	ICabType,
	ShowCabCapacityInfo,
} from "../CabTypes"
import {
	TTravelActivity,
	TTravelActivityDuration,
	TTravelActivityTicketType,
} from "../TravelActivities"
import { TTravelActivityBookingTicketTouristConfiguration } from "../TravelActivityBookings/store"
import { ITrip } from "../Trips"
import * as Validator from "yup"
import { TemporaryStorage } from "../TemporaryStorage"
import config from "../config"

type TOperationalBookingProps = {
	tripId: number | string
}

export function TripOperationalBookingsItem(props: TOperationalBookingProps) {
	const { tripId } = props
	const xhr = useXHR()
	const { hasPermission } = useCheckPermissions()
	const {
		data: tripDetails,
		mutate: revalidate,
		isValidating,
	} = useSWR<TTripOperationalBooking>(
		`/trip-operational-bookings/${tripId}`,
		() =>
			xhr
				.get(`/trip-operational-bookings/${tripId}`)
				.then((resp) => resp.data.data)
	)
	const travelActivityBookings = useMemo(() => {
		if (!tripDetails) return []
		return tripDetails?.daywise_services
			.flatMap((d) => d.services || [])
			.map((s) => s.travel_activity_booking)
			.filter((b): b is Exclude<typeof b, undefined> => Boolean(b))
	}, [tripDetails])
	if (!tripDetails) return <Spinner padding="4" alignCenter />
	const {
		daywise_services,
		cab_schedules: schedules,
		is_shared,
		can_modify,
		cancellation_reason,
		can_view_prices,
		is_locked,
	} = tripDetails
	const showPrices = Boolean(!is_shared && can_view_prices)
	const can_Edit = Boolean(!cancellation_reason && can_modify && !is_locked)
	return (
		<Stack gap="4" cursor={isValidating ? "wait" : undefined}>
			<Inline
				gap="4"
				collapseBelow="md"
				justifyContent="between"
				position="relative"
				zIndex="50"
			>
				<Heading as="h4">Operational Services</Heading>
				<Inline gap="2" justifyContent="end" flexWrap={"wrap"}>
					{tripDetails.travel_activity_bookings?.length ||
					tripDetails.cab_schedules?.length ? (
						<>
							{!is_shared && can_Edit ? (
								<>
									{schedules?.length !== 0 ? (
										<AssignServiceProvidersInDialog
											tripId={tripDetails.id}
											onSuccess={() => revalidate()}
										>
											{({ assign }) => (
												<Button
													onClick={() => assign()}
													status="primary"
													size="sm"
												>
													<Icons.Driver /> Update Cab Supplier(s){" "}
													<Icons.NewFeature size={"10"} />
												</Button>
											)}
										</AssignServiceProvidersInDialog>
									) : null}
									{travelActivityBookings.length ? (
										<AssignTravelActivityBookingProvidersInDialog
											bookings={travelActivityBookings}
											onSuccess={() => revalidate()}
										>
											{({ assign }) => (
												<Button
													onClick={() => assign()}
													status="primary"
													size="sm"
												>
													<Icons.Driver /> Update Activity Supplier(s)
												</Button>
											)}
										</AssignTravelActivityBookingProvidersInDialog>
									) : null}
									<UpdateTransportAndActivityTimingsInDialog
										tripId={tripDetails.id}
										onSuccess={() => revalidate()}
									>
										{({ assign }) => (
											<Button
												onClick={() => assign()}
												status="primary"
												size="sm"
											>
												<Icons.Clock /> Update Timings{" "}
												<Icons.NewFeature size={"10"} />
											</Button>
										)}
									</UpdateTransportAndActivityTimingsInDialog>
								</>
							) : null}
							{!is_shared ? (
								<ShareTripOperationalBookings trip={tripDetails}>
									{({ share }) => (
										<Button
											onClick={() => share()}
											level="secondary"
											title="Share Bookings"
											size="sm"
										>
											<Icons.Share />
										</Button>
									)}
								</ShareTripOperationalBookings>
							) : null}
						</>
					) : null}
					{hasPermission(PERMISSIONS.VIEW_CAB_SCHEDULES) ? (
						<Dropdown alignRight>
							<Dropdown.ToggleButton level="tertiary" size="sm">
								<Icons.DotsVertical />
							</Dropdown.ToggleButton>
							<Dropdown.Menu>
								<NavLink
									to={`${generatePath(
										"/operational-bookings/calendar"
									)}${queryToSearch({
										date: dateToQuery(
											localOrUtcTimestampToLocalDate(
												tripDetails.start_date_local,
												tripDetails.start_date
											)
										),
									})}`}
								>
									<Icons.Calendar /> View on Calendar
								</NavLink>
								<TemporaryStorage
									initUrl={"/trip-operational-bookings/download"}
									initParams={{
										trip_id: tripDetails.id,
										start_date: tripDetails.start_date,
										start_date_local: tripDetails.start_date_local,
										end_date: tripDetails.end_date,
										end_date_local: tripDetails.end_date_local,
										timezone_offset: config.timezoneOffset,
									}}
								>
									{({ generate, url, message, reset }) => (
										<Dropdown.MenuItem
											onClick={!url ? generate : () => reset()}
											href={url || "#"}
											target={url ? "_blank" : undefined}
										>
											{message || (
												<>
													<Icons.DocumentDownload /> Download Services
												</>
											)}
										</Dropdown.MenuItem>
									)}
								</TemporaryStorage>

								{!can_Edit ? null : (
									<>
										<NavLink
											to={generatePath(
												"/trips/:tripId/services-bookings/operational/new",
												{
													tripId: tripDetails.id.toString(),
												}
											)}
										>
											<Icons.Plus /> Add More Bookings
										</NavLink>
										<Dropdown.MenuItemDivider />
										<Component initialState={false}>
											{({ state: isUpdating, setState: setIsUpdating }) => (
												<Dropdown.MenuItem
													onClick={(e: React.MouseEvent) => {
														e.preventDefault()
														if (isUpdating) {
															return false
														}
														if (
															window.confirm(
																"Operational bookings will be updated using the latest Quote from this Trip.\n\nAre you sure you want to continue?"
															)
														) {
															setIsUpdating(true)
															xhr
																.post(
																	`/trip-operational-bookings/${tripDetails.id}/sync-with-latest-quote`
																)
																.then(() => {
																	showSnackbar(
																		"Operational bookings have been synced with Latest Quote"
																	)
																})
																.catch((error) => {
																	const e = error as Error
																	alert(
																		e.message ||
																			"Something went wrong. Please try after sometime."
																	)
																})
																.then(() => {
																	// always revalidate
																	revalidate()
																	setIsUpdating(false)
																})
														}
													}}
												>
													<Icons.Refresh spin={isUpdating} /> Sync Bookings with
													Quote
												</Dropdown.MenuItem>
											)}
										</Component>
									</>
								)}
							</Dropdown.Menu>
						</Dropdown>
					) : null}
				</Inline>
			</Inline>
			{!daywise_services.length ? (
				<Text>No Operational Services for this Trip</Text>
			) : (
				daywise_services.map(
					({
						date,
						date_local,
						day,
						services,
						services_needs_reordering,
						services_ordered_by_start_time,
					}) => (
						<Inline position="relative" gap="4" collapseBelow="sm" key={date}>
							<Box position="sticky" top="0" zIndex="10">
								<Box position="sticky" top="0" zIndex="10">
									<TripOperationalBookingsItemForDateInDialog
										day={day}
										tripId={tripId}
										tripStartDateUtc={tripDetails.start_date}
										date={localOrUtcTimestampToLocalDate(date_local, date)}
										onRefresh={() => revalidate()}
									>
										{({ show }) => (
											<Button inline onClick={() => show()} fullWidth>
												<Box
													bgColor="default"
													textAlign="center"
													paddingX="4"
													paddingY="1"
													rounded="md"
													borderWidth={"1"}
													style={{ minWidth: "110px" }}
													width="full"
												>
													<Text fontWeight="semibold" color="accent">
														{withOrdinalSuffix(day)} Day
													</Text>
													<Time
														timestamp={date}
														localTimestamp={date_local}
														format="ddd, D MMM"
														whiteSpace="preserve"
														fontSize="sm"
													/>
												</Box>
											</Button>
										)}
									</TripOperationalBookingsItemForDateInDialog>
								</Box>
							</Box>
							<Box flex="1">
								<Stack gap="4">
									{services_needs_reordering ? (
										<Alert status="warning" title="Services needs Re-Ordering">
											<Stack gap="1">
												<Text>
													Some services are not aligned with the provided
													timings of the services.
												</Text>
												<Box>
													<ReorderServicesInDialog
														tripId={tripDetails.id}
														services={services}
														initialOrdering={services_ordered_by_start_time}
														onSuccess={() => {
															revalidate()
														}}
													>
														{({ onReorder }) => (
															<Button
																onClick={() => onReorder()}
																status="warning"
																size="sm"
																level="primary"
															>
																<Icons.SwitchHorizontal rotate="90" /> Re-Order
																Now
															</Button>
														)}
													</ReorderServicesInDialog>
												</Box>
											</Stack>
										</Alert>
									) : null}
									<DayBookingServicesOverview
										services={services}
										onRefresh={() => revalidate()}
										showPrices={showPrices}
										children={tripDetails.children}
										noOfAdults={tripDetails.no_of_adults}
									/>
								</Stack>
							</Box>
						</Inline>
					)
				)
			)}
		</Stack>
	)
}

export function DayBookingServicesOverview({
	services,
	onRefresh,
	showPrices,
	compactView,
	children,
	noOfAdults,
}: {
	services: TTripOperationalBooking["daywise_services"][number]["services"]
	onRefresh: () => unknown
	showPrices: boolean
	compactView?: boolean
	children: Array<{ age: number }> | undefined
	noOfAdults: number | undefined
}) {
	return (
		<Stack bgColor="default" rounded="md" borderWidth="1">
			{services.map(({ cab_schedule, travel_activity_booking }, i) => {
				if (cab_schedule) {
					return (
						<Box
							key={`cab-schedule-${cab_schedule.id}`}
							padding={compactView ? "2" : "4"}
							borderTopWidth={i > 0 ? "1" : undefined}
						>
							<CabScheduleListItem
								cabSchedule={cab_schedule}
								onRefresh={() => onRefresh()}
								showPrices={showPrices}
								compactView={compactView}
								children={children}
								noOfAdults={noOfAdults}
							/>
						</Box>
					)
				} else if (travel_activity_booking) {
					return (
						<Box
							padding={compactView ? "2" : "4"}
							key={`travel-activity-booking-${travel_activity_booking.id}`}
							borderTopWidth={i > 0 ? "1" : undefined}
						>
							<TravelActivityBookingListItem
								activityBooking={travel_activity_booking}
								hidePrice={!showPrices}
								onRefresh={() => onRefresh()}
								compactView={compactView}
							/>
						</Box>
					)
				}
				return null
			})}
		</Stack>
	)
}

function CabScheduleListItem({
	cabSchedule,
	onRefresh,
	showPrices,
	compactView,
	children,
	noOfAdults,
}: {
	cabSchedule: Required<
		TTripOperationalBooking["daywise_services"][number]["services"][number]
	>["cab_schedule"]
	onRefresh: () => unknown
	showPrices?: boolean
	compactView?: boolean
	children: Array<{ age: number }> | undefined
	noOfAdults: number | undefined
}) {
	const {
		transport_service,
		cabs,
		duration_formatted,
		remarks,
		comments,
		has_cab_time_variation,
	} = cabSchedule
	const startTime = useMemo(() => {
		return getStartDateTimeOfCabSchedule(cabSchedule)
	}, [cabSchedule])

	const endTime = useMemo(() => {
		return getEndDateTimeOfCabSchedule(cabSchedule)
	}, [cabSchedule])

	return (
		<Grid gap="4">
			<Col xs={12} sm={4}>
				<Stack gap="px">
					<ShareTripOperationalBookings
						trip={{ id: cabSchedule.trip_id }}
						cabSchedules={[cabSchedule]}
					>
						{({ share }) => (
							<ScheduleDetailsInDialog
								schedule={cabSchedule}
								onRefresh={onRefresh}
								onShare={share}
							>
								{({ onShow }) => (
									<Text<"a">
										as="a"
										href="#schedule-details"
										fontWeight="semibold"
										color="accent"
										onClick={(e) => {
											e.preventDefault()
											onShow()
										}}
									>
										<Stack>
											<Box>
												{transport_service.name}
												{transport_service.deleted_at ? (
													<Box>
														<Badge warning>
															<Icons.Ban />
														</Badge>
													</Box>
												) : null}
											</Box>
											{transport_service.service_short_name ? (
												<Text color="muted" fontSize="sm" fontWeight="normal">
													[[{transport_service.service_short_name}]]
												</Text>
											) : null}
										</Stack>
									</Text>
								)}
							</ScheduleDetailsInDialog>
						)}
					</ShareTripOperationalBookings>
					{(startTime || duration_formatted) && !compactView ? (
						<Inline gap="1" alignItems="center">
							<Icons.Clock size="3" />
							<Box flex="1" minWidth="0">
								<Text fontSize="sm">
									{startTime ? (
										<Time value={startTime} format="HH:mm [hrs]" />
									) : null}{" "}
									{duration_formatted ? (
										<>
											({duration_formatted}{" "}
											{startTime && endTime ? (
												<>
													- Ends <Time value={endTime} format="HH:mm [hrs]" />
												</>
											) : null}
											)
										</>
									) : null}
								</Text>
							</Box>
						</Inline>
					) : null}
					{!compactView && (remarks || comments) ? (
						<Stack gap="1">
							{remarks ? (
								<Box as="blockquote" marginBottom="0">
									{remarks}
								</Box>
							) : null}
							{comments ? (
								<Text fontSize="sm" color="muted" title="Internal Comments">
									<Icons.Annotation /> {comments}
								</Text>
							) : null}
						</Stack>
					) : null}
				</Stack>
			</Col>
			<Col>
				<Stack
					style={{
						// negative of every list item
						margin: "-.5rem -1rem -.5rem 0",
					}}
				>
					{cabs.map(
						(
							{
								id,
								cab,
								cab_type,
								driver,
								transport_service_provider,
								booked,
								confirmation_details,
								pickup_time_local,
								dropoff_time_local,
								duration_formatted,
								remarks,
								comments,
								...cabSchedule
							},
							index
						) => (
							<Inline
								key={id}
								borderTopWidth={index > 0 ? "1" : undefined}
								paddingY="2"
								paddingRight="4"
								gap="4"
								justifyContent="between"
							>
								<Grid gap="4">
									<Col xs={12} lg={5}>
										<Stack gap="2">
											<Inline gap="1">
												{booked ? (
													<Box>
														<Icons.OkCircleSolid color="success" />
													</Box>
												) : null}
												<Stack>
													<Inline gap="1" flexWrap="wrap" minWidth="0">
														<Text fontWeight="semibold">
															{cab?.name || cab_type.display_name}
														</Text>
														{cab?.number_plate && !compactView ? (
															<Box
																borderWidth={"1"}
																borderRadius="md"
																paddingX={"2"}
																paddingY={"1"}
																fontSize="sm"
																fontWeight="semibold"
																color={"warning"}
																borderColor={"warning"}
															>
																{cab.number_plate}
															</Box>
														) : cab_type.short_name ? (
															<Text color="muted">
																[[{cab_type.short_name}]]
															</Text>
														) : null}
													</Inline>

													<ShowCabCapacityInfo cabType={cab_type} />
												</Stack>
											</Inline>
											{compactView ? null : confirmation_details ? (
												<Inline>
													<Box
														whiteSpace="preserveAndWrap"
														borderWidth="1"
														paddingY="1"
														paddingX="2"
														rounded="md"
														fontSize="sm"
														bgColor="inset"
													>
														{confirmation_details}
													</Box>
												</Inline>
											) : null}
											{has_cab_time_variation &&
											(pickup_time_local || duration_formatted) &&
											!compactView ? (
												<Inline gap="1" alignItems="center">
													<Icons.Clock size="3" />
													<Box flex="1" minWidth="0">
														<Text fontSize="sm">
															{pickup_time_local ? (
																<Text>
																	{formatDate(
																		parseDate(pickup_time_local, "HH:mm:ss"),
																		"HH:mm [hrs]"
																	)}
																</Text>
															) : null}{" "}
															{duration_formatted ? (
																<>
																	({duration_formatted} Mins{" "}
																	{pickup_time_local && dropoff_time_local ? (
																		<>
																			- Ends{" "}
																			{formatDate(
																				parseDate(
																					dropoff_time_local,
																					"HH:mm:ss"
																				),
																				"HH:mm [hrs]"
																			)}
																		</>
																	) : null}
																	)
																</>
															) : null}
														</Text>
													</Box>
												</Inline>
											) : null}
											{!compactView && (remarks || comments) ? (
												<Stack gap="1">
													{remarks ? (
														<Box as="blockquote" marginBottom="0">
															{remarks}
														</Box>
													) : null}
													{comments ? (
														<Text
															fontSize="sm"
															color="muted"
															title="Internal Comments"
														>
															<Icons.Annotation /> {comments}
														</Text>
													) : null}
												</Stack>
											) : null}
										</Stack>
									</Col>
									<Col>
										<Grid gap="2">
											{transport_service_provider ? (
												<Col xs={12} md={6}>
													<Inline gap="1" flexWrap="wrap">
														<Box fontWeight="semibold">
															<Link
																to={generatePath(
																	"/transport-service-providers/:transportServiceProviderId",
																	{
																		transportServiceProviderId:
																			transport_service_provider.id.toString(),
																	}
																)}
															>
																{transport_service_provider.name}
															</Link>
														</Box>
														<Box>
															{transport_service_provider.contacts?.length
																? joinAttributes(
																		transport_service_provider.contacts[0]
																			.phone_numbers?.length ? (
																			<PhoneNumber
																				value={
																					transport_service_provider.contacts[0]
																						.phone_numbers
																				}
																				iconOnly
																			/>
																		) : null,
																		transport_service_provider.contacts[0]
																			.email ? (
																			<Email
																				value={
																					transport_service_provider.contacts[0]
																						.email
																				}
																				iconOnly
																			/>
																		) : null
																	)
																: null}
														</Box>
													</Inline>
												</Col>
											) : null}
											{driver ? (
												<Col>
													<Inline gap="2" alignItems="center">
														<Box>
															<Icons.SteeringWheel />
														</Box>
														<Box fontWeight="semibold">{driver.name}</Box>
														<Box color="muted">
															{joinAttributes(
																driver.phone_number ? (
																	<PhoneNumber
																		value={driver.phone_number}
																		iconOnly
																	/>
																) : null,
																driver.email ? (
																	<Email value={driver.email} iconOnly />
																) : null
															)}
														</Box>
													</Inline>
												</Col>
											) : null}
										</Grid>
									</Col>
								</Grid>
								{showPrices ? (
									<BookingAmount booking={{ ...cabSchedule, booked }} />
								) : null}
							</Inline>
						)
					)}
					<Box>
						<Box display={"inlineBlock"}>
							<CabCapacityWarningMessage
								children={children}
								messageType="withPax"
								cabs={cabs}
								noOfAdults={noOfAdults ?? 0}
							/>
						</Box>
					</Box>
				</Stack>
			</Col>
		</Grid>
	)
}

function TravelActivityBookingListItem({
	activityBooking,
	hidePrice,
	onRefresh,
	compactView,
}: {
	activityBooking: Required<
		TTripOperationalBooking["daywise_services"][number]["services"][number]
	>["travel_activity_booking"]
	hidePrice?: boolean
	onRefresh?: () => void
	compactView?: boolean
}) {
	const {
		start_time_formatted,
		end_time_formatted,
		activity,
		ticket_type,
		duration_formatted,
		ticket_tourist_configurations,
		supplier,
		supplier_contact,
		confirmation_details,
		remarks,
		comments,
		is_booked,
	} = activityBooking

	return (
		<Grid gap="4">
			<Col xs={12} sm={4}>
				<Stack gap="px">
					<ShareTripOperationalBookings
						trip={{ id: activityBooking.trip_id }}
						travelActivityBookings={[activityBooking]}
					>
						{({ share }) => (
							<TravelActivityBookingDetailsInDialog
								booking={activityBooking}
								onRefresh={onRefresh}
								onShare={share}
							>
								{({ onShow }) => (
									<Text<"a">
										as="a"
										href="travel-activity-booking-details"
										fontWeight="semibold"
										color="accent"
										onClick={(e) => {
											e.preventDefault()
											onShow()
										}}
									>
										<Stack>
											<Box>
												{activity.name}{" "}
												{ticket_type ? (
													<Text as="span">
														- {ticket_type.display_name}{" "}
														{ticket_type.deleted_at ? (
															<Badge warning>
																<Icons.Ban /> Disabled
															</Badge>
														) : null}
													</Text>
												) : null}
											</Box>
											{ticket_type?.short_name ? (
												<Text fontSize="sm" color="muted" fontWeight="normal">
													[[{ticket_type.short_name}]]
												</Text>
											) : null}
										</Stack>
									</Text>
								)}
							</TravelActivityBookingDetailsInDialog>
						)}
					</ShareTripOperationalBookings>
					{!compactView && (start_time_formatted || duration_formatted) ? (
						<Inline gap="1" alignItems="center">
							<Icons.Clock size="3" />
							<Box flex="1" minWidth="0">
								<Text fontSize="sm">
									{joinAttributes(
										start_time_formatted,
										duration_formatted
											? `${duration_formatted}${end_time_formatted ? ` (Ends: ${end_time_formatted})` : ``}`
											: null
									)}
								</Text>
							</Box>
						</Inline>
					) : null}

					{!compactView && (remarks || comments) ? (
						<Stack gap="1">
							{remarks ? (
								<Box as="blockquote" marginBottom="0">
									{remarks}
								</Box>
							) : null}
							{comments ? (
								<Text fontSize="sm" color="muted" title="Internal Comments">
									<Icons.Annotation /> {comments}
								</Text>
							) : null}
						</Stack>
					) : null}
				</Stack>
			</Col>
			<Col>
				<Stack gap="2">
					<Inline justifyContent="between" gap="4">
						<Grid gap="4">
							<Col xs={12} lg={5}>
								<Inline gap="1">
									{is_booked ? (
										<Box title="Booked">
											<Icons.OkCircleSolid color="success" />
										</Box>
									) : null}
									<Inline gap="4" flexWrap="wrap">
										{ticket_tourist_configurations.map(
											(
												{
													configuration,
													quantity,
													calculated_price,
													per_quantity_booked_price,
													booked_price,
													currency,
												},
												index
											) => (
												<Stack key={`${activity.id}-${index}`}>
													<Text fontWeight="semibold">
														{quantity} {configuration.name}
													</Text>
													{hidePrice || compactView ? null : (
														<Box fontSize="sm">
															{per_quantity_booked_price ? (
																<Text>
																	<Money
																		color={
																			calculated_price &&
																			Number(calculated_price) ===
																				Number(booked_price)
																				? "success"
																				: "muted"
																		}
																		amount={per_quantity_booked_price}
																		currency={currency}
																	/>{" "}
																	* {quantity}
																</Text>
															) : (
																<Text color="warning">N/A</Text>
															)}
														</Box>
													)}
												</Stack>
											)
										)}
									</Inline>
									{is_booked && compactView && !confirmation_details ? (
										<Box title="Missing Confirmation Details">
											<Icons.AttentionSolid color="warning" />
										</Box>
									) : null}
								</Inline>
							</Col>
							{supplier || supplier_contact ? (
								<Col>
									<Grid gap="2">
										<Col xs={12} md={6}>
											{supplier ? (
												<Inline gap="1" flexWrap="wrap">
													<Box fontWeight="semibold">
														<Link
															to={generatePath(
																"/transport-service-providers/:transportServiceProviderId",
																{
																	transportServiceProviderId:
																		supplier.id.toString(),
																}
															)}
														>
															{supplier.name}
														</Link>
													</Box>
													<Box>
														{supplier.contacts?.length
															? joinAttributes(
																	supplier.contacts[0].phone_numbers?.length ? (
																		<PhoneNumber
																			value={supplier.contacts[0].phone_numbers}
																			iconOnly
																		/>
																	) : null,
																	supplier.contacts[0].email ? (
																		<Email
																			value={supplier.contacts[0].email}
																			iconOnly
																		/>
																	) : null
																)
															: null}
													</Box>
												</Inline>
											) : null}
										</Col>
										{supplier_contact ? (
											<Col>
												<Inline gap="2" flexWrap="wrap" alignItems="center">
													<Box>
														<Icons.User />
													</Box>
													<Box fontWeight="semibold">
														{supplier_contact.name}
													</Box>
													<Box>
														{supplier_contact.phone_numbers?.length ||
														supplier_contact.email
															? joinAttributes(
																	supplier_contact.phone_numbers?.length ? (
																		<PhoneNumber
																			value={supplier_contact.phone_numbers}
																			iconOnly
																		/>
																	) : null,
																	supplier_contact.email ? (
																		<Email
																			value={supplier_contact.email}
																			iconOnly
																		/>
																	) : null
																)
															: null}
													</Box>
													{supplier_contact.deleted_at ? (
														<>
															<Icons.Ban
																color="warning"
																title={`Disabled on ${utcTimestampToLocalDateTimeString(supplier_contact.deleted_at)}`}
															/>
														</>
													) : null}
												</Inline>
											</Col>
										) : null}
									</Grid>
								</Col>
							) : null}
						</Grid>
						{!hidePrice ? (
							<Box textAlign={"right"}>
								<BookingAmount booking={activityBooking} />
							</Box>
						) : null}
					</Inline>
					{compactView ? null : confirmation_details ? (
						<Inline>
							<Box
								whiteSpace="preserveAndWrap"
								borderWidth="1"
								paddingY="1"
								paddingX="2"
								rounded="md"
								fontSize="sm"
								bgColor="inset"
							>
								{confirmation_details}
							</Box>
						</Inline>
					) : null}
				</Stack>
			</Col>
		</Grid>
	)
}

export function BookingAmount({
	booking,
}: {
	booking: IPaymentable & {
		currency: string
		booked_price?: number
		booked?: boolean
	}
}) {
	const {
		currency,
		booked_price,
		payments_amount,
		refunding_payments_amount,
		cancellation_charges,
		paid_payments_amount,
		paid_refunding_payments_amount,
	} = booking
	if (
		!isNumeric(booked_price) &&
		!isNumeric(payments_amount) &&
		!isNumeric(paid_payments_amount) &&
		!isNumeric(refunding_payments_amount)
	) {
		return null
	}
	return (
		<Stack alignItems={"end"}>
			{isNumeric(booked_price) ? (
				<Inline
					fontWeight="semibold"
					gap="1"
					alignItems="center"
					justifyContent="end"
				>
					{refunding_payments_amount === null ? (
						// If not in refund mode, show Green Check when
						// - when booked
						// AND, when all payments are paid.
						// In case payments_amount is null, paid_payments_amount must also be null.
						booking.booked && paid_payments_amount === payments_amount ? (
							<Box display="inlineBlock" marginLeft="1">
								<Icons.OkCircleSolid color="success" />
							</Box>
						) : null
					) : paid_refunding_payments_amount === refunding_payments_amount ? (
						// If in refund mode, show Green Check when
						// - Refund is complete
						<Box display="inlineBlock" marginLeft="1">
							<Icons.OkCircleSolid color="success" />
						</Box>
					) : null}
					<Text>Booking:</Text>
					<Money amount={booked_price} currency={currency} showCurrency />
				</Inline>
			) : null}
			{isNumeric(payments_amount) ? (
				<Box fontWeight="semibold" fontSize="sm" color="muted">
					{isNumeric(refunding_payments_amount) &&
					isNumeric(cancellation_charges) ? (
						<Box title="Net Payable" whiteSpace="nowrap">
							Net Payable:{" "}
							<Money
								amount={cancellation_charges}
								currency={currency}
								showCurrency
							/>
						</Box>
					) : isNumeric(booked_price) &&
					  Number(payments_amount || 0) ===
							Number(booked_price || 0) ? null : (
						<Box title="Net Payable" whiteSpace="nowrap">
							Net Payable:{" "}
							<Money
								amount={payments_amount || 0}
								currency={currency}
								showCurrency
							/>
						</Box>
					)}
				</Box>
			) : //show net payable as 0 if booked is true
			booking.booked ? (
				<Box
					fontWeight="semibold"
					fontSize="sm"
					color="muted"
					whiteSpace="nowrap"
					title="Net Payable"
				>
					Net Payable: <Money amount={0} currency={currency} showCurrency />
				</Box>
			) : null}
			{isNumeric(paid_payments_amount) ? (
				<>
					<Box fontWeight="semibold" fontSize="sm" color="success">
						Amount Paid:{" "}
						<Money
							amount={paid_payments_amount}
							currency={currency}
							showCurrency
						/>
					</Box>
					{isNumeric(payments_amount) &&
						payments_amount > paid_payments_amount && (
							<Box fontWeight="semibold" fontSize="sm" color="muted">
								Due Payable:{" "}
								<Money
									amount={payments_amount - paid_payments_amount}
									currency={currency}
									showCurrency
								/>
							</Box>
						)}
				</>
			) : //show paid=0 when "paid_payments_amount" is null && "payments_amount" is not null & "payments_amount" is greater than 0
			isNumeric(payments_amount) && Number(payments_amount || 0) > 0 ? (
				<Box fontWeight="semibold" fontSize="sm" color="muted">
					Amount Paid: <Money amount={0} currency={currency} showCurrency />
				</Box>
			) : null}
			{isNumeric(refunding_payments_amount) ? (
				<Box
					fontWeight="semibold"
					fontSize="sm"
					color="warning"
					title="Refund"
					whiteSpace="nowrap"
				>
					Refund:{" "}
					<Money
						amount={paid_refunding_payments_amount || 0}
						currency={currency}
						showCurrency
					/>
					{" / "}
					<Money
						amount={refunding_payments_amount}
						currency={currency}
						showCurrency
					/>
				</Box>
			) : null}
		</Stack>
	)
}

type UpdateTransportAndActivityBookingsTimingsProps = {
	tripId: number
	onSuccess: (data: unknown) => void
	onCancel: () => void
}

interface ITransportAndActivityTimingsBookingsAssignPayload {
	type: "cab_schedule" | "travel_activity_booking"
	id: number
	start_time_local?: string
	duration?: string
	remarks?: string
}

function UpdateTransportAndActivityTimingsInDialog({
	children,
	onSuccess,
	...props
}: { children: (props: { assign: () => void }) => React.ReactNode } & Optional<
	Omit<UpdateTransportAndActivityBookingsTimingsProps, "onCancel">,
	"onSuccess"
>) {
	const [isOpen, open, close] = useDialog()
	return (
		<>
			{children({ assign: () => open() })}
			<Dialog
				open={isOpen}
				onClose={close}
				title="Update Transport Service and Activity Timings"
				lg
			>
				<Dialog.Body bgColor={"inset"} paddingX="4" paddingY={"2"}>
					<UpdateTransportAndActivityTimings
						onSuccess={(...args) => {
							onSuccess?.(...args)
							close()
						}}
						onCancel={close}
						{...props}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function UpdateTransportAndActivityTimings({
	tripId,
	onSuccess,
	onCancel,
}: UpdateTransportAndActivityBookingsTimingsProps) {
	const xhr = useXHR()
	const { data: tripDetails } = useSWR<TTripOperationalBooking>(
		`/trip-operational-bookings/${tripId}`,
		() =>
			xhr
				.get(`/trip-operational-bookings/${tripId}`)
				.then((resp) => resp.data.data)
	)
	if (!tripDetails) {
		return <Spinner padding="4" alignCenter />
	}
	const { daywise_services } = tripDetails
	if (!daywise_services.length) {
		return (
			<Box textAlign="center" padding="4">
				No travel service and activities for this trip
			</Box>
		)
	}
	return (
		<UpdateTransportAndActivityTimingsSchedulesForm
			daywise_services={daywise_services}
			trip={{
				id: tripDetails.id,
				start_date_local: tripDetails.start_date_local,
				end_date: tripDetails.end_date,
			}}
			onCancel={onCancel}
			onSubmit={async (d) => {
				const resp = await xhr.post(
					`/trip-operational-bookings/${tripId}/assign-timings`,
					{
						data: d,
					}
				)
				onSuccess(resp.data.data)
			}}
		/>
	)
}

interface IFormUpdateDaywiseServicesActivitiesTimings {
	data: Array<{
		date_config: {
			date: string
			day: number
			date_local?: string
		}
		id: number
		type: "cab_schedule" | "travel_activity_booking"
		start_time_local?: string
		duration?: {
			iso: string
			name: string
		}
		remarks?: string
		has_cab_time_variation?: boolean
		show_cabs_remarks?: boolean
		cab_schedule?: {
			transport_service: ITransportService
			transport_service_location: TTransportServiceLocation
			cab_types?: Array<{
				type: ICabType
				count: number
				booked_count: number
				not_booked_count: number
				all_booked: boolean
			}>
			cabs?: Array<{
				id: number
				cab_type: ICabType
				start_time_local?: string
				duration?: {
					iso: string
					name: string
				}
				remarks?: string
			}>
		}
		travel_activity_booking?: {
			travel_activity: TTravelActivity
			ticket_type?: TTravelActivityTicketType
			confirmation_details?: string
			is_booked?: boolean
			ticket_tourist_configurations: TTravelActivityBookingTicketTouristConfiguration[]
			available_slots?: Array<string>
		}
	}>
}

function canAddDuration(pickup: Date) {
	return formatDate(pickup, "HH:mm") !== "00:01"
}

export function UpdateTransportAndActivityTimingsSchedulesForm({
	daywise_services,
	trip,
	onCancel,
	onSubmit,
}: {
	daywise_services: Array<TDaywiseOperationalBookingService>
	trip?: Pick<ITrip, "id" | "start_date_local" | "end_date" | "end_date_local">
	onCancel: () => void
	onSubmit: (
		data: Array<ITransportAndActivityTimingsBookingsAssignPayload>
	) => Promise<void>
}) {
	const initialFormValues: IFormUpdateDaywiseServicesActivitiesTimings = {
		data: daywise_services.flatMap((daywise_service) => {
			const transport_services = daywise_service.services
				.filter(
					(service): service is Required<typeof service, "cab_schedule"> =>
						service.cab_schedule !== null
				)
				.map((service) => service.cab_schedule)

			const travel_activities = daywise_service.services
				.filter(
					(
						service
					): service is Required<typeof service, "travel_activity_booking"> =>
						service.travel_activity_booking !== null
				)
				.map((service) => service.travel_activity_booking)

			const transportServiceEntries = transport_services.map((cab_schedule) => {
				const {
					id,
					start_date,
					start_date_local,
					start_time_local,
					duration_iso,
					remarks,
					transport_service,
					cab_types,
					has_cab_time_variation,
					cabs,
				} = cab_schedule
				const pickup = (() => {
					const startDateTime = getStartDateTimeOfCabSchedule({
						start_date,
						start_date_local,
						start_time_local,
					})
					return (
						startDateTime ||
						addUnit(
							startOf(
								localOrUtcTimestampToLocalDate(start_date_local, start_date),
								"day"
							),
							1,
							"minute"
						)
					).toISOString()
				})()

				const pickup_date = parseDate(
					pickup
						? pickup
						: startOf(
								trip
									? localOrUtcTimestampToLocalDate(
											trip.start_date_local,
											trip.end_date
										)
									: new Date(),
								"day"
							)
				)

				const duration = duration_iso
					? {
							iso: duration_iso,
							name: createDuration(duration_iso).asMinutes() + " Mins",
						}
					: undefined

				const date = formatDate(new Date(daywise_service.date), "YYYY-MM-DD")

				// Process cabs data
				const cabsData = cabs?.map((cab) => ({
					id: cab.id,
					cab_type: cab.cab_type,
					start_time_local: cab.pickup_time_local
						? formatDate(parseDate(cab.pickup_time_local, "HH:mm"), "HH:mm")
						: undefined,
					duration: cab.duration_iso
						? {
								iso: cab.duration_iso,
								name: createDuration(cab.duration_iso).asMinutes() + " Mins",
							}
						: undefined,
					remarks: cab.remarks || "",
				}))

				// Check if any cab has remarks to show the cab remarks section by default
				const hasCabRemarks = cabsData?.some((cab) => cab.remarks) || false

				return {
					date_config: {
						date,
						day: daywise_service.day,
						date_local: daywise_service.date_local,
					},
					id,
					type: "cab_schedule" as const,
					start_time_local: canAddDuration(pickup_date)
						? formatDate(pickup_date, "HH:mm")
						: undefined,
					duration,
					remarks: remarks,
					has_cab_time_variation: has_cab_time_variation || false,
					show_cabs_remarks: hasCabRemarks,
					cab_schedule: {
						transport_service,
						transport_service_location: transport_service.locations,
						cab_types,
						cabs: cabsData,
					},
				}
			})

			const travelActivityEntries = travel_activities.map((travel_activity) => {
				const date = formatDate(new Date(daywise_service.date), "YYYY-MM-DD")
				const { duration, slot, ticket_type } = travel_activity

				let durationOption: TTravelActivityDuration | undefined =
					ticket_type?.durations?.find((d) => d.iso && d.iso === duration)
				let activitySlot: string | undefined = slot
				let activitySlotsOptions: Array<string> | undefined = undefined

				if (duration && !durationOption) {
					const parseDuration = createDuration(duration)
					durationOption = {
						name: parseDuration.asMinutes() + " Mins",
						iso: duration,
						slots: slot ? [slot] : [],
					}
				}

				if (durationOption && !slot && durationOption.slots?.length === 1) {
					activitySlot = durationOption.slots[0]
				}

				if (slot && !durationOption) {
					activitySlotsOptions = [slot]
				} else {
					if (durationOption && durationOption?.slots?.length) {
						activitySlotsOptions = durationOption.slots
					}

					if (!slot && ticket_type?.durations?.at(0)?.slots?.length) {
						activitySlotsOptions = ticket_type?.durations?.at(0)?.slots
					}
				}

				return {
					date_config: {
						date,
						day: daywise_service.day,
						date_local: daywise_service.date_local,
					},
					type: "travel_activity_booking" as const,
					id: travel_activity.id,
					start_time_local: activitySlot,
					duration: durationOption?.iso
						? {
								iso: durationOption.iso as string,
								name: durationOption.name as string,
							}
						: undefined,
					remarks: travel_activity.remarks,
					travel_activity_booking: {
						travel_activity: travel_activity.activity,
						ticket_type: travel_activity.ticket_type,
						ticket_tourist_configurations:
							travel_activity.ticket_tourist_configurations,
						available_slots: activitySlotsOptions,
						confirmation_details: travel_activity.confirmation_details,
						is_booked: travel_activity.is_booked,
					},
				}
			})

			return [...transportServiceEntries, ...travelActivityEntries]
		}),
	}

	return (
		<Form<IFormUpdateDaywiseServicesActivitiesTimings>
			initialValues={initialFormValues}
			validate={validateFormValues(
				Validator.object().shape({
					data: Validator.array().of(
						Validator.object().shape({
							start_time_local: Validator.string().nullable(),
							duration: Validator.object()
								.shape({
									iso: Validator.string(),
									name: Validator.string(),
								})
								.nullable(),
							remarks: Validator.string().nullable(),
						})
					),
				})
			)}
			onSubmit={withServerErrors(async (values) => {
				const payload = values.data.map((d) => {
					const start_time_local = d.start_time_local
						? parseDate(d.start_time_local, "HH:mm")
						: undefined

					const basePayload: ITransportAndActivityTimingsBookingsAssignPayload & {
						has_cab_time_variation?: number
						start_date_local?: string
						cabs?: Array<{
							id: number
							pickup_time_local: string | null
							duration: string | null
							remarks: string | null
						}>
					} = {
						id: d.id,
						type: d.type,
						start_time_local:
							start_time_local && !d.has_cab_time_variation
								? formatDate(start_time_local, "HH:mm")
								: "",
						duration:
							d.duration?.iso && !d.has_cab_time_variation
								? d.duration.iso
								: "",
						remarks: d.remarks || "",
					}

					// Add cab-specific data for cab_schedule type
					if (d.type === "cab_schedule" && d.cab_schedule?.cabs) {
						basePayload.has_cab_time_variation = d.has_cab_time_variation
							? 1
							: 0
						basePayload.start_date_local = d.date_config.date_local
						basePayload.cabs = d.cab_schedule.cabs.map((cab) => {
							const cab_start_time = cab.start_time_local
								? parseDate(cab.start_time_local, "HH:mm")
								: undefined

							return {
								id: cab.id,
								pickup_time_local:
									d.has_cab_time_variation && cab_start_time
										? formatDate(cab_start_time, config.timeFormat)
										: null,
								duration:
									d.has_cab_time_variation && cab.duration?.iso
										? cab.duration.iso
										: null,
								remarks: cab.remarks || null,
							}
						})
					}

					return basePayload
				})
				await onSubmit(payload)
			})}
			mutators={{ ...arrayMutators }}
			subscription={{ submitting: true }}
		>
			{({ handleSubmit, submitting, form }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Box>
						<FieldArray<
							Required<IFormUpdateDaywiseServicesActivitiesTimings>["data"][number]
						> name="data">
							{({ fields }) => {
								return (
									<Stack paddingBottom={"4"}>
										{fields.map((name, index) => (
											<Inline
												key={name}
												gap={"2"}
												flexDirection={{
													lg: "row",
													xs: "col",
												}}
												position="relative"
												collapseBelow="sm"
											>
												<GetFieldValue<
													IFormUpdateDaywiseServicesActivitiesTimings["data"][number]["date_config"]
												>
													name={`data[${index - 1}].date_config`}
												>
													{({ value: previous_date_config }) => {
														return (
															<GetFieldValue<
																IFormUpdateDaywiseServicesActivitiesTimings["data"][number]["date_config"]
															>
																name={`${name}.date_config`}
															>
																{({ value: date_config }) => {
																	const isSameDay =
																		previous_date_config?.day ===
																		date_config?.day

																	if (isSameDay) {
																		return (
																			<Box style={{ minWidth: "110px" }}></Box>
																		)
																	}

																	return (
																		<Box
																			position="sticky"
																			top="0"
																			zIndex="10"
																			style={{ width: "110px" }}
																			marginTop={!isSameDay ? "2" : "0"}
																		>
																			<Box
																				bgColor="default"
																				textAlign="center"
																				paddingX="2"
																				paddingY="1"
																				rounded="md"
																				borderWidth={"1"}
																			>
																				<Text
																					fontWeight="semibold"
																					color="accent"
																				>
																					{withOrdinalSuffix(date_config.day)}{" "}
																					Day
																				</Text>
																				<Time
																					timestamp={date_config.date}
																					localTimestamp={
																						date_config.date_local
																					}
																					format="ddd, D MMM"
																					whiteSpace="preserve"
																					fontSize="sm"
																					fontWeight="semibold"
																				/>
																			</Box>
																		</Box>
																	)
																}}
															</GetFieldValue>
														)
													}}
												</GetFieldValue>

												<GetFieldValue<
													IFormUpdateDaywiseServicesActivitiesTimings["data"][number]["date_config"]
												>
													name={`data[${index - 1}].date_config`}
												>
													{({ value: previous_date_config }) => {
														return (
															<GetFieldValue<
																IFormUpdateDaywiseServicesActivitiesTimings["data"][number]["date_config"]
															>
																name={`${name}.date_config`}
															>
																{({ value: date_config }) => {
																	const isSameDay =
																		previous_date_config?.day ===
																		date_config?.day

																	return (
																		<Box key={name} flex={"1"}>
																			<GetFieldValue<
																				IFormUpdateDaywiseServicesActivitiesTimings["data"][number]
																			>
																				name={name}
																			>
																				{({ value: service }) => {
																					if (
																						service.type === "cab_schedule" &&
																						service.cab_schedule
																					) {
																						return (
																							<Box
																								paddingX={"2"}
																								paddingY={"2"}
																								borderColor="muted"
																								roundedTop={
																									index === 0 ? "md" : "none"
																								}
																								width="full"
																								bgColor={"default"}
																								borderBottomWidth={
																									fields.length === index + 1
																										? "0"
																										: "2"
																								}
																								roundedBottom={
																									fields.length === index + 1
																										? "md"
																										: "none"
																								}
																								marginTop={
																									!isSameDay ? "2" : "0"
																								}
																							>
																								<Grid gap={"2"}>
																									{/* Left side */}
																									<Col sm={12} md>
																										<Stack
																											width={"full"}
																											gap="2"
																										>
																											<Inline
																												width={"full"}
																												gap="2"
																												alignItems="center"
																											>
																												<Box
																													padding="2"
																													bgColor="accent"
																													borderRadius="full"
																													style={{
																														alignSelf: "start",
																													}}
																												>
																													<Icons.Taxi size="5" />
																												</Box>
																												<Stack flex="1" gap="2">
																													<Stack gap="1">
																														<Text
																															fontWeight="semibold"
																															fontSize="md"
																														>
																															{
																																service
																																	?.cab_schedule
																																	?.transport_service
																																	.name
																															}
																														</Text>
																														<Box
																															fontSize="sm"
																															color="muted"
																															fontWeight="semibold"
																														>
																															{service?.cab_schedule?.cab_types?.map(
																																(
																																	cab_type,
																																	index
																																) => (
																																	<Text
																																		key={index}
																																	>
																																		{`${
																																			cab_type.count
																																		} ${
																																			cab_type
																																				.type
																																				.full_name
																																		}`}
																																	</Text>
																																)
																															)}
																														</Box>
																													</Stack>
																													<GetFieldValue
																														name={`${name}.remarks`}
																													>
																														{({
																															value: remarks,
																														}) => {
																															return (
																																<Component
																																	initialState={
																																		remarks
																																			? true
																																			: false
																																	}
																																>
																																	{({
																																		state:
																																			showCommentsInput,
																																		setState:
																																			setShowCommentsInput,
																																	}) => (
																																		<Inline
																																			gap="2"
																																			alignItems="center"
																																		>
																																			{!showCommentsInput ? (
																																				<Button
																																					inline
																																					size="sm"
																																					onClick={() =>
																																						setShowCommentsInput(
																																							true
																																						)
																																					}
																																					level="tertiary"
																																				>
																																					<Icons.Plus />{" "}
																																					Remarks
																																				</Button>
																																			) : (
																																				<Box flex="1">
																																					<TextAreaInputField
																																						name={`${name}.remarks`}
																																						rows={
																																							1
																																						}
																																						label="Remarks for Supplier and Customer"
																																						placeholder="Any notes/remarks that should be shared with Supplier and Customer..."
																																						maxWidth={
																																							"md"
																																						}
																																					/>
																																				</Box>
																																			)}
																																			<GetFieldValue<boolean>
																																				name={`${name}.show_cabs_remarks`}
																																			>
																																				{({
																																					value:
																																						showCabsRemarks,
																																				}) => (
																																					<Box alignSelf="center">
																																						<Icons.DotsVertical
																																							onClick={() =>
																																								form.change(
																																									`${name}.show_cabs_remarks` as never,
																																									!showCabsRemarks as never
																																								)
																																							}
																																							cursor="pointer"
																																							title="Add cabwise remarks"
																																						/>
																																					</Box>
																																				)}
																																			</GetFieldValue>
																																		</Inline>
																																	)}
																																</Component>
																															)
																														}}
																													</GetFieldValue>
																												</Stack>
																											</Inline>
																										</Stack>
																									</Col>

																									{/* Right side */}

																									<Col sm={12} md>
																										<Stack gap="2">
																											<GetFieldValue<boolean>
																												name={`${name}.has_cab_time_variation`}
																											>
																												{({
																													value:
																														hasCabTimeVariation,
																												}) => (
																													<Inline
																														gap="4"
																														flexWrap={{
																															xs: "wrap",
																															lg: "nowrap",
																														}}
																														justifyContent={{
																															xl: "end",
																														}}
																														paddingY={
																															isSameDay
																																? "2"
																																: "0"
																														}
																														opacity={
																															hasCabTimeVariation
																																? "50"
																																: undefined
																														}
																														pointerEvents={
																															hasCabTimeVariation
																																? "none"
																																: undefined
																														}
																													>
																														<SelectField
																															label={
																																!isSameDay
																																	? "Start Time"
																																	: ""
																															}
																															secondaryLabel="24 hrs"
																															select={Select}
																															name={`${name}.start_time_local`}
																															clearable={true}
																															minWidth="auto"
																															placeholder="14:00"
																															menuPlaceholder="Type time and select"
																															creatable
																															closeOnSelect
																															createOptionLabel={(
																																query
																															) => {
																																let date
																																try {
																																	date =
																																		parseDate(
																																			query,
																																			"HH:mm"
																																		)
																																} catch (e) {
																																	date =
																																		undefined
																																}
																																return date
																																	? `Set: ${formatDate(date, "HH:mm")} hrs`
																																	: "Invalid Time"
																															}}
																															onCreateNew={(
																																query
																															) => {
																																let date
																																try {
																																	date =
																																		parseDate(
																																			query,
																																			"HH:mm"
																																		)
																																} catch (e) {
																																	date =
																																		undefined
																																}
																																return date
																																	? formatDate(
																																			date,
																																			"HH:mm"
																																		)
																																	: ""
																															}}
																														/>

																														<Box>
																															<SelectField
																																minWidth="auto"
																																label={
																																	!isSameDay
																																		? "Duration"
																																		: ""
																																}
																																placeholder="60 Mins"
																																select={Select}
																																name={`${name}.duration`}
																																creatable={true}
																																createOptionLabel={(
																																	q
																																) => q}
																																onCreateNew={(
																																	query
																																) => {
																																	let durationISO =
																																		""
																																	try {
																																		durationISO =
																																			createDuration(
																																				query.replace(
																																					/[^\d]/g,
																																					""
																																				),
																																				"minutes"
																																			).toISOString()
																																	} catch (e) {
																																		durationISO =
																																			""
																																	}
																																	let duration:
																																		| {
																																				iso: string
																																				name: string
																																		  }
																																		| undefined =
																																		undefined
																																	if (
																																		durationISO
																																	) {
																																		duration = {
																																			iso: durationISO,
																																			name:
																																				createDuration(
																																					durationISO
																																				).asMinutes() +
																																				" Mins",
																																		}
																																	}
																																	return duration
																																}}
																															/>
																														</Box>
																													</Inline>
																												)}
																											</GetFieldValue>
																											<Box>
																												<SwitchInputField
																													name={`${name}.has_cab_time_variation`}
																													label="Cabwise Timing"
																												/>
																											</Box>
																										</Stack>
																									</Col>
																								</Grid>
																								<GetFieldValue<boolean>
																									name={`${name}.has_cab_time_variation`}
																								>
																									{({
																										value: hasCabTimeVariation,
																									}) =>
																										(service.show_cabs_remarks ||
																											hasCabTimeVariation) &&
																										service.cab_schedule
																											?.cabs ? (
																											<Stack
																												gap="3"
																												paddingTop="2"
																											>
																												{service.cab_schedule.cabs.map(
																													(cab, cabIndex) => (
																														<Box
																															key={cabIndex}
																															padding="3"
																															borderTopWidth="1"
																														>
																															<Grid>
																																<Col
																																	sm={12}
																																	md={6}
																																>
																																	<Stack gap="2">
																																		<Text fontWeight="semibold">
																																			{
																																				cab
																																					.cab_type
																																					.name
																																			}
																																		</Text>
																																		<GetFieldValue<
																																			ICabSchedule["cabs"][number]
																																		>
																																			name={`${name}.cab_schedule.cabs[${cabIndex}]`}
																																		>
																																			{({
																																				value:
																																					cab,
																																			}) => {
																																				if (
																																					!cab
																																						.cab_type
																																						.display_name
																																				)
																																					return null

																																				return (
																																					<Component
																																						initialState={
																																							cab.remarks
																																								? true
																																								: false
																																						}
																																					>
																																						{({
																																							state:
																																								showCabRemarksInput,
																																							setState:
																																								setShowCabRemarksInput,
																																						}) => (
																																							<Box>
																																								{!showCabRemarksInput ? (
																																									<Button
																																										inline
																																										size="sm"
																																										onClick={() =>
																																											setShowCabRemarksInput(
																																												true
																																											)
																																										}
																																										level="tertiary"
																																									>
																																										<Icons.Plus />{" "}
																																										Remarks
																																									</Button>
																																								) : (
																																									<TextAreaInputField
																																										name={`${name}.cab_schedule.cabs[${cabIndex}].remarks`}
																																										label="Cabwise Remarks"
																																										secondaryLabel="optional"
																																										placeholder="Any notes/remarks that should be shared with Supplier and Customer..."
																																										maxWidth="md"
																																									/>
																																								)}
																																							</Box>
																																						)}
																																					</Component>
																																				)
																																			}}
																																		</GetFieldValue>
																																	</Stack>
																																</Col>

																																{hasCabTimeVariation ? (
																																	<Col
																																		sm={12}
																																		md={6}
																																	>
																																		<Inline gap="3">
																																			<SelectField
																																				label="Start Time"
																																				secondaryLabel="24 hrs"
																																				select={
																																					Select
																																				}
																																				name={`${name}.cab_schedule.cabs[${cabIndex}].start_time_local`}
																																				clearable={
																																					true
																																				}
																																				minWidth="auto"
																																				placeholder="14:00"
																																				menuPlaceholder="Type time and select"
																																				creatable
																																				closeOnSelect
																																				createOptionLabel={(
																																					query
																																				) => {
																																					let date
																																					try {
																																						date =
																																							parseDate(
																																								query,
																																								"HH:mm"
																																							)
																																					} catch (e) {
																																						date =
																																							undefined
																																					}
																																					return date
																																						? `Set: ${formatDate(date, "HH:mm")} hrs`
																																						: "Invalid Time"
																																				}}
																																				onCreateNew={(
																																					query
																																				) => {
																																					let date
																																					try {
																																						date =
																																							parseDate(
																																								query,
																																								"HH:mm"
																																							)
																																					} catch (e) {
																																						date =
																																							undefined
																																					}
																																					return date
																																						? formatDate(
																																								date,
																																								"HH:mm"
																																							)
																																						: ""
																																				}}
																																			/>

																																			<SelectField
																																				minWidth="auto"
																																				label="Duration"
																																				placeholder="60 Mins"
																																				select={
																																					Select
																																				}
																																				name={`${name}.cab_schedule.cabs[${cabIndex}].duration`}
																																				creatable={
																																					true
																																				}
																																				createOptionLabel={(
																																					q
																																				) => q}
																																				onCreateNew={(
																																					query
																																				) => {
																																					let durationISO =
																																						""
																																					try {
																																						durationISO =
																																							createDuration(
																																								query.replace(
																																									/[^\d]/g,
																																									""
																																								),
																																								"minutes"
																																							).toISOString()
																																					} catch (e) {
																																						durationISO =
																																							""
																																					}
																																					let duration:
																																						| {
																																								iso: string
																																								name: string
																																						  }
																																						| undefined =
																																						undefined
																																					if (
																																						durationISO
																																					) {
																																						duration =
																																							{
																																								iso: durationISO,
																																								name:
																																									createDuration(
																																										durationISO
																																									).asMinutes() +
																																									" Mins",
																																							}
																																					}
																																					return duration
																																				}}
																																			/>
																																		</Inline>
																																	</Col>
																																) : null}
																															</Grid>
																														</Box>
																													)
																												)}
																											</Stack>
																										) : null
																									}
																								</GetFieldValue>
																							</Box>
																						)
																					}

																					if (
																						service.type ===
																							"travel_activity_booking" &&
																						service.travel_activity_booking
																					) {
																						return (
																							<Box
																								padding={"2"}
																								roundedTop={
																									index === 0 ? "md" : "none"
																								}
																								borderColor="muted"
																								width="full"
																								bgColor={"default"}
																								borderBottomWidth={
																									fields.length === index + 1
																										? "0"
																										: "2"
																								}
																								roundedBottom={
																									fields.length === index + 1
																										? "md"
																										: "none"
																								}
																								marginTop={
																									!isSameDay ? "2" : "0"
																								}
																							>
																								<Grid gap="2">
																									{/* Left side */}
																									<Col sm={12} md>
																										<Stack
																											gap="2"
																											width={"full"}
																										>
																											<Inline
																												width={"full"}
																												gap="2"
																												alignItems={"center"}
																											>
																												<Box
																													padding="2"
																													bgColor="accent"
																													borderRadius="full"
																													style={{
																														alignSelf: "start",
																													}}
																												>
																													<Icons.Ticket size="5" />
																												</Box>
																												<Stack
																													flex={"1"}
																													gap="2"
																												>
																													<Stack gap="1">
																														<Text
																															fontWeight="semibold"
																															fontSize="md"
																														>
																															{
																																service
																																	.travel_activity_booking
																																	.travel_activity
																																	.name
																															}{" "}
																															{service
																																.travel_activity_booking
																																.travel_activity
																																.deleted_at ? (
																																<Badge warning>
																																	<Icons.Ban />{" "}
																																	Disabled
																																</Badge>
																															) : null}
																															{service
																																.travel_activity_booking
																																.ticket_type ? (
																																<Text as="span">
																																	-{" "}
																																	{
																																		service
																																			.travel_activity_booking
																																			.ticket_type
																																			.display_name
																																	}
																																	{service
																																		.travel_activity_booking
																																		.ticket_type
																																		.deleted_at ? (
																																		<Badge
																																			warning
																																		>
																																			<Icons.Ban />{" "}
																																			Disabled
																																		</Badge>
																																	) : null}
																																</Text>
																															) : null}
																														</Text>
																														<Inline
																															gap="1"
																															fontSize="sm"
																															color="muted"
																															fontWeight="semibold"
																														>
																															{service
																																.travel_activity_booking
																																.is_booked ? (
																																<Box title="Booked">
																																	<Icons.OkCircleSolid color="success" />
																																</Box>
																															) : null}
																															<Inline
																																gap="4"
																																flexWrap="wrap"
																															>
																																{service.travel_activity_booking.ticket_tourist_configurations.map(
																																	(
																																		{
																																			configuration,
																																			quantity,
																																		},
																																		index
																																	) => (
																																		<Stack
																																			key={`${configuration.id}-${index}`}
																																		>
																																			<Text>
																																				{
																																					quantity
																																				}{" "}
																																				{
																																					configuration.name
																																				}
																																			</Text>
																																		</Stack>
																																	)
																																)}
																															</Inline>
																														</Inline>
																													</Stack>
																													<GetFieldValue
																														name={`${name}.remarks`}
																													>
																														{({
																															value: remarks,
																														}) => {
																															return (
																																<Component
																																	initialState={
																																		remarks
																																			? true
																																			: false
																																	}
																																>
																																	{({
																																		state:
																																			showCommentsInput,
																																		setState:
																																			setShowCommentsInput,
																																	}) => (
																																		<Box>
																																			{!showCommentsInput ? (
																																				<Button
																																					inline
																																					size="sm"
																																					onClick={() =>
																																						setShowCommentsInput(
																																							true
																																						)
																																					}
																																					level="tertiary"
																																				>
																																					<Icons.Plus />{" "}
																																					Remarks
																																				</Button>
																																			) : (
																																				<TextAreaInputField
																																					name={`${name}.remarks`}
																																					maxWidth={
																																						"md"
																																					}
																																					label="Remarks for Supplier and Customer"
																																					placeholder="Notes and remarks to be shared with supplier and customer..."
																																				/>
																																			)}
																																		</Box>
																																	)}
																																</Component>
																															)
																														}}
																													</GetFieldValue>
																												</Stack>
																											</Inline>
																										</Stack>
																									</Col>

																									<GetFieldValue<
																										IFormUpdateDaywiseServicesActivitiesTimings["data"][number]["date_config"]
																									>
																										name={`data[${index - 1}].date_config`}
																									>
																										{({
																											value:
																												previous_date_config,
																										}) => {
																											return (
																												<GetFieldValue<
																													IFormUpdateDaywiseServicesActivitiesTimings["data"][number]["date_config"]
																												>
																													name={`${name}.date_config`}
																												>
																													{({
																														value: date_config,
																													}) => {
																														const isSameDay =
																															previous_date_config?.day ===
																															date_config?.day

																														return (
																															<Col sm={12} md>
																																<Inline
																																	gap="4"
																																	flexWrap={{
																																		xs: "wrap",
																																		lg: "nowrap",
																																	}}
																																	justifyContent={{
																																		xl: "end",
																																	}}
																																	paddingY={
																																		isSameDay
																																			? "2"
																																			: "0"
																																	}
																																>
																																	<SelectField
																																		label={
																																			!isSameDay
																																				? "Start Time"
																																				: ""
																																		}
																																		select={
																																			Select
																																		}
																																		name={`${name}.start_time_local`}
																																		clearable={
																																			true
																																		}
																																		options={
																																			service
																																				?.travel_activity_booking
																																				?.available_slots
																																		}
																																		size="sm"
																																		placeholder="14:00"
																																		createOptionLabel={(
																																			q
																																		) => q}
																																		creatable
																																		closeOnSelect
																																		onCreateNew={(
																																			query
																																		) => {
																																			let date
																																			try {
																																				date =
																																					parseDate(
																																						query,
																																						"HH:mm"
																																					)
																																			} catch (e) {
																																				date =
																																					undefined
																																			}
																																			return date
																																				? formatDate(
																																						date,
																																						"HH:mm"
																																					)
																																				: ""
																																		}}
																																	/>

																																	<SelectField
																																		label={
																																			!isSameDay
																																				? "Duration"
																																				: ""
																																		}
																																		select={
																																			Select
																																		}
																																		name={`${name}.duration`}
																																		placeholder="60 Mins"
																																		createOptionLabel={(
																																			q
																																		) => q}
																																		options={
																																			service
																																				.travel_activity_booking
																																				?.ticket_type
																																				?.durations
																																		}
																																		creatable
																																		size="sm"
																																		onCreateNew={(
																																			query
																																		) => {
																																			let durationISO =
																																				""
																																			try {
																																				durationISO =
																																					createDuration(
																																						query.replace(
																																							/[^\d]/g,
																																							""
																																						),
																																						"minutes"
																																					).toISOString()
																																			} catch (e) {
																																				durationISO =
																																					""
																																			}
																																			let duration:
																																				| TTravelActivityDuration
																																				| undefined =
																																				undefined
																																			if (
																																				durationISO
																																			) {
																																				duration =
																																					{
																																						iso: durationISO,
																																						name:
																																							createDuration(
																																								durationISO
																																							).asMinutes() +
																																							" Mins",
																																						slots:
																																							[],
																																					}
																																			}
																																			return duration
																																		}}
																																	/>
																																</Inline>
																															</Col>
																														)
																													}}
																												</GetFieldValue>
																											)
																										}}
																									</GetFieldValue>
																								</Grid>
																							</Box>
																						)
																					}

																					return null
																				}}
																			</GetFieldValue>
																		</Box>
																	)
																}}
															</GetFieldValue>
														)
													}}
												</GetFieldValue>
											</Inline>
										))}
									</Stack>
								)
							}}
						</FieldArray>
						<SubmissionError />
						<Dialog.Footer paddingX="0">
							<Inline
								style={{
									marginLeft: "118px",
								}}
								gap="4"
							>
								<Button onClick={onCancel} disabled={submitting}>
									Cancel
								</Button>
								<Button type="submit" status="primary" disabled={submitting}>
									{submitting ? "Saving..." : "Save Changes"}
								</Button>
							</Inline>
						</Dialog.Footer>
					</Box>
				</form>
			)}
		</Form>
	)
}
